import {
  PackageSchemaTypeEnum,
  PackageStatusDetailsLevelEnum,
  PackageStatusDetailsStatusEnum,
  PackageTypeEnum,
} from '../domain';
import { PackageMeta } from '../PackageMeta';
import { z } from 'zod';

const categoriesList = z.array(
  z.object({
    id: z.string().nullable(),
    count: z.number(),
    name: z.string().optional(),
  }),
);

const PackageReleases = z
  .array(
    z.object({
      status: z.nativeEnum(PackageStatusDetailsStatusEnum),
      level: z.nativeEnum(PackageStatusDetailsLevelEnum),
      version: z.string(),
      primaryId: z.number().optional(),
    }),
  )
  .default([]);

export const GetPackagesResponseEntity = z.object({
  createdAt: z.date().or(z.string()),
  id: z.string(),
  isExternal: z.boolean(),
  isSandbox: z.boolean(),
  isPlayground: z.boolean(),
  latest: PackageMeta,
  latestVersion: z.string(),
  level: z.nativeEnum(PackageStatusDetailsLevelEnum).optional(),
  levels: z.array(z.nativeEnum(PackageStatusDetailsLevelEnum)).optional(),
  name: z.string().optional(),
  releases: PackageReleases,
  latestDocId: z.any(),
  revisionId: z.string(),
  schemaType: z.nativeEnum(PackageSchemaTypeEnum),
  status: z.string().optional(),
  statuses: z.array(z.string()).optional(),
  type: z.nativeEnum(PackageTypeEnum),
  versions: z.array(z.string()),
});

export const GetPackagesResponse = z.object({
  entities: z.array(GetPackagesResponseEntity),
  total: z.number(),
  count: z.number(), // TODO: remove this
  offset: z.number(),
  limit: z.number(),
  cursor: z.object({
    from: z.number(),
  }),
  categories: categoriesList,
  all: z.object({
    categories: categoriesList,
    count: z.number(),
  }),
});

export const PackageStatusDetails = z
  .object({
    release: z.object({
      pde: z
        .object({
          status: z.array(z.nativeEnum(PackageStatusDetailsStatusEnum)),
        })
        .optional(),
      user: z
        .object({
          status: z.array(z.nativeEnum(PackageStatusDetailsStatusEnum)),
        })
        .optional(),
      account: z
        .object({
          status: z.array(z.nativeEnum(PackageStatusDetailsStatusEnum)),
        })
        .optional(),
    }),
    system: z
      .object({
        status: z.array(z.nativeEnum(PackageStatusDetailsStatusEnum)),
      })
      .optional(),
  })
  .optional();

export const GetPackageResponse = z.object({
  baseUrl: z.string(),
  data: z.any(),
  packageMeta: PackageMeta,
  id: z.string(),
  version: z.string(),
  schemaType: z.nativeEnum(PackageSchemaTypeEnum),
  revisionId: z.string(),
  status: z.nativeEnum(PackageStatusDetailsStatusEnum).optional(),
  level: z.nativeEnum(PackageStatusDetailsLevelEnum).optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  categories: z.array(z.string()).optional(),
  statusDetails: PackageStatusDetails,
});

export const GetPackageByIdIncludes = z.object({
  releaseNotes: z
    .object({ markdown: z.string().nullable().optional() })
    .optional()
    .optional(),
  member: z
    .object({
      account: z
        .array(
          z.object({
            accountId: z.string(),
          }),
        )
        .optional(),
      user: z
        .array(
          z.object({
            userId: z.string(),
          }),
        )
        .optional(),
    })
    .optional()
    .describe('Deprecated for v2 version api'),
  membersAccountIds: z.array(z.string()).optional(),
  membersUserIds: z.array(z.string()).optional(),
});

export const GetPackageByIdResponse = z.intersection(
  z.object({
    version: z.string(),
    baseUrl: z.string(),
    statusDetails: PackageStatusDetails.optional(),
    createdAt: z.date().or(z.string()).optional(),
    revisionId: z.string(),
    name: z.string().optional(),
    description: z.string().nullable().optional(),
    categories: z.array(z.string()).optional(),
    packageMeta: PackageMeta,
    id: z.string(),
    schemaType: z.nativeEnum(PackageSchemaTypeEnum),
  }),
  GetPackageByIdIncludes,
);

export const GetPackageByIdLatestResponse = z.intersection(
  z.object({
    packageMeta: PackageMeta,
    id: z.string(),
    schemaType: z.nativeEnum(PackageSchemaTypeEnum),
    version: z.string(),
    name: z.string().trim(),
    baseUrl: z.string(),
    releases: PackageReleases,
    revisionId: z.string(),
  }),
  GetPackageByIdIncludes,
);

export const GetPackageArchiveSignedUrl = z.object({
  key: z.string(),
  url: z.string(),
});

export const CreatePackageByVersionFileNamesResponse = z
  .array(
    z.object({
      fileName: z.string(),
      url: z.string(),
      fields: z.any(),
    }),
  )
  .optional()
  .describe(
    'When you upload a package with multiple files, this field will be returned',
  );

export const CreatePackageByVersionResponse = z.object({
  revisionId: z.string(),
  isExists: z.boolean(),
  fileNames: CreatePackageByVersionFileNamesResponse,
  url: z
    .string()
    .optional()
    .describe(
      'When you upload a package with a single file, this field will be returned',
    ),
  fields: z
    .any()
    .optional()
    .describe(
      'When you upload a package with a single file, this field will be returned',
    ),
});

export const GetPackageSystemStatusResponse = z.object({
  createdAt: z.date().or(z.string()),
  id: z.string(),
  revisionId: z.string(),
  statusDetailsSystem: z.array(
    z.object({
      status: z.nativeEnum(PackageStatusDetailsStatusEnum),
    }),
  ),
  type: z.nativeEnum(PackageTypeEnum),
  version: z.string(),
});

export const CreatePackageFromJsonResponse = z.object({
  success: z.boolean(),
  debug: z.any().optional(),
});
